import React from 'react';
import { AlertCircle, Thermometer, DollarSign, Clock, Wrench } from 'lucide-react';

interface Props {
  primaryColor?: string;
}

export default function FloridaPainPointsSection({ primaryColor = '#0066CC' }: Props) {
  const painPoints = [
    {
      icon: <Thermometer className="w-6 h-6" />,
      title: 'Your AC runs 8+ months per year in Florida',
      description: 'That\'s 3x more stress than up north — without maintenance, most Florida AC systems fail by year 8 (half their expected life)',
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: 'Unexpected breakdowns during peak summer',
      description: 'Emergency repairs can cost $500-$2,000+ and take 3-5 days to schedule — unless you\'re a member',
    },
    {
      icon: <AlertCircle className="w-6 h-6" />,
      title: 'Florida humidity breeds zoogleal slime',
      description: 'This biological growth clogs drain lines and destroys systems. Without treatment, it\'s Florida\'s #1 AC killer',
    },
    {
      icon: <Wrench className="w-6 h-6" />,
      title: 'Voided manufacturer warranties',
      description: 'Skipped maintenance voids most warranties, leaving you on the hook for expensive repairs',
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'System lifespan cut short by 5-7 years',
      description: 'Premature replacement costs $8,000-$15,000 — all preventable with regular tune-ups',
    },
  ];

  return (
    <section className="py-16 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Here\'s what happens without a maintenance plan...
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Florida\'s climate is uniquely harsh on AC systems. Every day without protection puts you at risk.
          </p>
        </div>

        {/* Pain Points Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {painPoints.map((point, idx) => (
            <div
              key={idx}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div
                className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4"
                style={{ backgroundColor: `${primaryColor}14`, color: primaryColor }}
              >
                {point.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{point.title}</h3>
              <p className="text-gray-600 leading-relaxed">{point.description}</p>
            </div>
          ))}
        </div>

        {/* Stat Bar */}
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl sm:text-4xl font-bold" style={{ color: primaryColor }}>
              8+
            </div>
            <div className="text-sm text-gray-600 mt-1">Months AC Runs</div>
          </div>
          <div className="text-center">
            <div className="text-3xl sm:text-4xl font-bold" style={{ color: primaryColor }}>
              $2,000
            </div>
            <div className="text-sm text-gray-600 mt-1">Avg Emergency Repair</div>
          </div>
          <div className="text-center">
            <div className="text-3xl sm:text-4xl font-bold" style={{ color: primaryColor }}>
              5-7
            </div>
            <div className="text-sm text-gray-600 mt-1">Years Lost Lifespan</div>
          </div>
          <div className="text-center">
            <div className="text-3xl sm:text-4xl font-bold" style={{ color: primaryColor }}>
              $15K
            </div>
            <div className="text-sm text-gray-600 mt-1">Premature Replacement</div>
          </div>
        </div>
      </div>
    </section>
  );
}
